<template>
   <div data-cy="education-block">
      <BlogpostContent
         v-if="props.block.data?.type === 'blog' && blogpostResp"
         class="-mt-10"
         :hasHeaderSection="!!props.block?.data?.hasHeaderSection"
         :blogpost="blogpostResp"
         :isTitleHidden="true"
      />
      <div v-else data-cy="content-fetch-error">
         {{ $t("survey.blogpost_not_found") }}
      </div>
   </div>
</template>

<script setup lang="ts">
import type { AnswerResp } from "@/data/api-layer/answersApi";
import type { AxiosError } from "axios";
import { useI18n } from "@/vue-i18n/vue-i18n";
import BlogpostsApi, { type Blogpost } from "@/data/api-layer/blogpostsApi";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

const { t: $t } = useI18n();

export interface EducationBlock extends FlowselBlock {
   data: {
      type: "blog" | "report";
      id: string;
      hasHeaderSection?: boolean;
   };
}

// information block will never show errors however its required by every other dynamic <component> block
interface Props {
   block: EducationBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits(["update-valid", "update-answer"]);

const state = reactive({
   blogpostId: props.block.data?.id,
});

const blogpostResp = ref<Blogpost | null>(null);

async function getBlogData() {
   try {
      // Admin/Team members should have access to unpublished content
      blogpostResp.value = await BlogpostsApi.getOneById(state.blogpostId);
   } catch (error) {
      if ((error as AxiosError).response.status === 404) {
         // In this instance we don't want to redirect as the participant is in the middle of a flow
         // Also it's nicer to show the 'sorry, we can't find this blogpost' in the flow area than to have a toast popup
         console.error(`Cannot fetch data for blogpost with id ${props.block?.data.id}`);
      }
   }
}

function getData() {
   // This block is not yet set up to fetch reports
   if (props.block.data?.type === "blog") {
      getBlogData();
   }
}
getData();

// We need to set 'valid' to true, as no answer will be entered
emit("update-valid", true);
</script>

<style scoped lang="scss"></style>
